import React, { useState, useEffect, useMemo } from 'react';
import { stakeholdersAPI, invitationsAPI } from '@/lib/api';
import { useProject } from '@/context/ProjectContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { toast } from 'sonner';
import {
  Users,
  UserCheck,
  Send,
  Pencil,
  MapPin,
  Calendar,
  X,
  XCircle,
  AlertTriangle,
  GitMerge,
  Copy,
  Trash2,
  Info
} from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

// Round to nice multiples for Y axis
function niceNumber(value) {
  if (value <= 0) return 0;
  const magnitude = Math.pow(10, Math.floor(Math.log10(value)));
  const residual = value / magnitude;
  let nice;
  if (residual <= 1) nice = magnitude;
  else if (residual <= 2) nice = 2 * magnitude;
  else if (residual <= 5) nice = 5 * magnitude;
  else nice = 10 * magnitude;
  return nice;
}

// Format date as DD-MMM
function formatDateDDMMM(dateStr) {
  const d = new Date(dateStr);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${String(d.getDate()).padStart(2, '0')}-${months[d.getMonth()]}`;
}

// User Growth Chart
function UserGrowthChart({ data, period, filter, selectedTotal }) {
  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center h-52 bg-slate-50 rounded-lg">
        <p className="text-slate-500 text-sm">No growth data available</p>
      </div>
    );
  }
  
  const displayData = data.slice(-20);
  
  // Determine which columns to include based on filter
  const includeAdmin = !filter || filter.col === 'total' || filter.col === 'admin';
  const includeUser = !filter || filter.col === 'total' || filter.col === 'user';
  const includeSelf = !filter || filter.col === 'total' || filter.col === 'self';
  const includeAnon = filter?.row === 'public';
  
  // Calculate period values respecting filter
  const getFilteredPeriodValue = (d) => {
    let total = 0;
    if (includeAnon) {
      total += (d.anonymous || 0);
    } else {
      if (includeAdmin) total += (d.admin || 0);
      if (includeUser) total += (d.user || 0);
      if (includeSelf) total += (d.self || 0);
    }
    return total;
  };
  
  // Sum all period values to get total additions over time
  const totalPeriodSum = displayData.reduce((sum, d) => sum + getFilteredPeriodValue(d), 0);
  
  // Scale factor: selectedTotal should be final cumulative value
  // If we have a selectedTotal, use it as the final cumulative target
  const finalCumulative = selectedTotal || totalPeriodSum;
  const scaleFactor = totalPeriodSum > 0 ? finalCumulative / totalPeriodSum : 1;
  
  // Build chart data with properly scaled cumulative
  let runningTotal = 0;
  const chartData = displayData.map((d, i) => {
    const periodValue = getFilteredPeriodValue(d);
    runningTotal += periodValue;
    // Scale the cumulative to match the selected total
    const scaledCumulative = Math.round(runningTotal * scaleFactor);
    return {
      ...d,
      filteredPeriod: periodValue,
      filteredCumulative: scaledCumulative
    };
  });
  
  // Calculate max period value for bar heights
  const rawMaxPeriod = Math.max(...chartData.map(d => d.filteredPeriod), 1);
  const maxPeriod = niceNumber(rawMaxPeriod);
  
  // Cumulative max is the final selected total
  const maxCumulative = niceNumber(finalCumulative);
  
  return (
    <div className="relative h-56 bg-slate-50 rounded-lg p-3 pb-8">
      {/* Left Y-axis (period - bars) */}
      <div className="absolute left-0 top-3 h-[140px] w-8 flex flex-col justify-between text-[10px] text-slate-600 text-right pr-1">
        <span>{maxPeriod}</span>
        <span>{Math.round(maxPeriod / 2)}</span>
        <span>0</span>
      </div>
      
      {/* Left axis label */}
      <div className="absolute left-0 top-1/2 -translate-y-1/2 -rotate-90 text-[9px] font-medium text-slate-500 whitespace-nowrap" style={{ transformOrigin: 'center', marginLeft: '-8px' }}>
        Period
      </div>
      
      {/* Right Y-axis (cumulative) */}
      <div className="absolute right-0 top-3 h-[140px] w-10 flex flex-col justify-between text-[10px] text-purple-600 text-left pl-1">
        <span>{maxCumulative}</span>
        <span>{Math.round(maxCumulative / 2)}</span>
        <span>0</span>
      </div>
      
      {/* Right axis label */}
      <div className="absolute right-0 top-1/2 -translate-y-1/2 rotate-90 text-[9px] font-medium text-purple-500 whitespace-nowrap" style={{ transformOrigin: 'center', marginRight: '-12px' }}>
        Cumulative
      </div>
      
      {/* Chart area */}
      <div className="mx-10 h-[140px] relative border-b border-l border-slate-300">
        {/* Grid lines */}
        <div className="absolute inset-0 flex flex-col justify-between pointer-events-none">
          <div className="border-b border-slate-200 border-dashed" />
          <div className="border-b border-slate-200 border-dashed" />
        </div>
        
        {/* Stacked bars */}
        <div className="flex items-end h-full">
          {chartData.map((d, i) => {
            // Calculate bar heights as percentage of chart height (140px)
            const adminVal = includeAdmin && !includeAnon ? (d.admin || 0) : 0;
            const userVal = includeUser && !includeAnon ? (d.user || 0) : 0;
            const selfVal = includeSelf && !includeAnon ? (d.self || 0) : 0;
            const anonVal = includeAnon ? (d.anonymous || 0) : 0;
            
            // Calculate heights in pixels (140px chart height)
            const chartHeightPx = 140;
            const adminH = maxPeriod > 0 ? (adminVal / maxPeriod) * chartHeightPx : 0;
            const userH = maxPeriod > 0 ? (userVal / maxPeriod) * chartHeightPx : 0;
            const selfH = maxPeriod > 0 ? (selfVal / maxPeriod) * chartHeightPx : 0;
            const anonH = maxPeriod > 0 ? (anonVal / maxPeriod) * chartHeightPx : 0;
            
            return (
              <div key={i} className="flex-1 relative group px-[1px] h-full">
                <div className="absolute bottom-0 left-0 right-0 flex flex-col-reverse">
                  {adminH > 0 && <div className="bg-blue-500" style={{ height: `${adminH}px`, minHeight: '2px' }} />}
                  {userH > 0 && <div className="bg-violet-500" style={{ height: `${userH}px`, minHeight: '2px' }} />}
                  {selfH > 0 && <div className="bg-teal-500" style={{ height: `${selfH}px`, minHeight: '2px' }} />}
                  {anonH > 0 && <div className="bg-slate-400" style={{ height: `${anonH}px`, minHeight: '2px' }} />}
                </div>
                
                {/* Tooltip */}
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 hidden group-hover:block z-20">
                  <div className="bg-slate-900 text-white text-[9px] rounded px-1.5 py-1 whitespace-nowrap shadow-lg">
                    <div className="font-medium mb-0.5">{formatDateDDMMM(d.date)}</div>
                    {!includeAnon && includeAdmin && <div>Admin: {d.admin || 0}</div>}
                    {!includeAnon && includeUser && <div>User: {d.user || 0}</div>}
                    {!includeAnon && includeSelf && <div>Self: {d.self || 0}</div>}
                    {includeAnon && <div>Public: {d.anonymous || 0}</div>}
                    <div className="border-t border-slate-700 mt-0.5 pt-0.5 text-purple-300">Cumulative: {d.filteredCumulative}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        
        {/* Cumulative line - SVG overlay */}
        <svg className="absolute inset-0 overflow-visible pointer-events-none" viewBox="0 0 100 100" preserveAspectRatio="none" style={{ width: '100%', height: '100%' }}>
          {/* Area fill under the line */}
          <polygon
            fill="rgba(147, 51, 234, 0.15)"
            points={`0,100 ${chartData.map((d, i) => {
              const x = ((i + 0.5) / chartData.length) * 100;
              const y = maxCumulative > 0 ? 100 - ((d.filteredCumulative / maxCumulative) * 100) : 100;
              return `${x},${y}`;
            }).join(' ')} 100,${maxCumulative > 0 ? 100 - ((chartData[chartData.length-1]?.filteredCumulative / maxCumulative) * 100) : 100} 100,100`}
          />
          {/* Line path - extend to right edge */}
          <polyline
            fill="none"
            stroke="#9333ea"
            strokeWidth="1"
            strokeLinecap="round"
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
            points={[
              ...chartData.map((d, i) => {
                const x = ((i + 0.5) / chartData.length) * 100;
                const y = maxCumulative > 0 ? 100 - ((d.filteredCumulative / maxCumulative) * 100) : 100;
                return `${x},${y}`;
              }),
              // Extend line to right edge at final cumulative value
              `100,${maxCumulative > 0 ? 100 - ((chartData[chartData.length-1]?.filteredCumulative / maxCumulative) * 100) : 100}`
            ].join(' ')}
          />
          {/* Dots on line */}
          {chartData.map((d, i) => {
            const x = ((i + 0.5) / chartData.length) * 100;
            const y = maxCumulative > 0 ? 100 - ((d.filteredCumulative / maxCumulative) * 100) : 100;
            return (
              <circle
                key={i}
                cx={x}
                cy={y}
                r="1.5"
                fill="#9333ea"
                className="drop-shadow-sm"
              />
            );
          })}
          {/* Final dot at right edge */}
          <circle
            cx="100"
            cy={maxCumulative > 0 ? 100 - ((chartData[chartData.length-1]?.filteredCumulative / maxCumulative) * 100) : 100}
            r="2"
            fill="#9333ea"
            stroke="white"
            strokeWidth="0.5"
          />
        </svg>
      </div>
      
      {/* X-axis dates */}
      <div className="flex justify-between mx-10 mt-1 text-[9px] text-slate-500">
        <span>{chartData[0]?.date ? formatDateDDMMM(chartData[0].date) : ''}</span>
        {chartData.length > 10 && (
          <span>{chartData[Math.floor(chartData.length/2)]?.date ? formatDateDDMMM(chartData[Math.floor(chartData.length/2)].date) : ''}</span>
        )}
        <span>{chartData[chartData.length-1]?.date ? formatDateDDMMM(chartData[chartData.length-1].date) : ''}</span>
      </div>
      
      {/* Legend */}
      <div className="flex justify-center gap-3 mt-2 text-[9px]">
        {!includeAnon && includeAdmin && <span className="flex items-center gap-1"><span className="w-2 h-2 bg-blue-500 rounded-sm"></span>Admin</span>}
        {!includeAnon && includeUser && <span className="flex items-center gap-1"><span className="w-2 h-2 bg-violet-500 rounded-sm"></span>User</span>}
        {!includeAnon && includeSelf && <span className="flex items-center gap-1"><span className="w-2 h-2 bg-teal-500 rounded-sm"></span>Self</span>}
        {includeAnon && <span className="flex items-center gap-1"><span className="w-2 h-2 bg-slate-400 rounded-sm"></span>Public</span>}
        <span className="flex items-center gap-1"><span className="w-2 h-2 bg-purple-600 rounded-full"></span>Cumulative ({finalCumulative})</span>
      </div>
    </div>
  );
}

// Segment Breakdown Chart
function SegmentBreakdownChart({ stakeholders, segmentTypes, segments, filter }) {
  const segmentCounts = useMemo(() => {
    const counts = {};
    segmentTypes.forEach(st => {
      counts[st.id] = { name: st.name, segments: {} };
      segments.filter(s => s.segment_type_id === st.id).forEach(seg => {
        counts[st.id].segments[seg.id] = { name: seg.name, count: 0 };
      });
    });
    
    stakeholders.forEach(s => {
      (s.segment_assignments || []).forEach(sa => {
        if (counts[sa.segment_type_id]?.segments[sa.segment_id]) {
          counts[sa.segment_type_id].segments[sa.segment_id].count++;
        }
      });
    });
    return counts;
  }, [stakeholders, segmentTypes, segments]);
  
  const maxCount = useMemo(() => {
    let max = 1;
    Object.values(segmentCounts).forEach(st => {
      Object.values(st.segments).forEach(seg => {
        if (seg.count > max) max = seg.count;
      });
    });
    return max;
  }, [segmentCounts]);
  
  if (!filter || stakeholders.length === 0) {
    return (
      <div className="flex items-center justify-center h-52 bg-slate-50 rounded-lg">
        <p className="text-slate-500 text-sm">Select a category to view segments</p>
      </div>
    );
  }
  
  const colors = ['bg-blue-500', 'bg-purple-500', 'bg-teal-500', 'bg-amber-500', 'bg-rose-500'];
  
  return (
    <div className="space-y-2 max-h-52 overflow-y-auto pr-1">
      {Object.entries(segmentCounts).map(([typeId, typeData], typeIdx) => {
        const segsWithCounts = Object.entries(typeData.segments).filter(([_, seg]) => seg.count > 0);
        if (segsWithCounts.length === 0) return null;
        
        return (
          <div key={typeId} className="space-y-0.5">
            <div className="text-[10px] font-medium text-slate-600">{typeData.name}</div>
            {segsWithCounts.map(([segId, seg], segIdx) => (
              <div key={segId} className="flex items-center gap-1">
                <div className="w-16 text-[9px] text-slate-500 truncate" title={seg.name}>{seg.name}</div>
                <div className="flex-1 h-3 bg-slate-100 rounded overflow-hidden relative">
                  <div className={`h-full ${colors[(typeIdx + segIdx) % colors.length]}`}
                    style={{ width: `${(seg.count / maxCount) * 100}%` }} />
                  <span className="absolute right-1 top-0 text-[9px] text-slate-600 leading-3">{seg.count}</span>
                </div>
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}

// Matrix cell
function MatrixCell({ value, isClickable, isSelected, onClick, highlight }) {
  if (value === undefined || value === null || value === '-') {
    return <TableCell className="text-center text-slate-300 py-1.5 text-sm">-</TableCell>;
  }
  
  return (
    <TableCell 
      className={`text-center font-medium tabular-nums py-1.5 text-sm ${isClickable && value > 0 ? 'cursor-pointer hover:bg-slate-100' : ''} ${isSelected ? 'bg-[#5AA2AE]/20 ring-1 ring-[#5AA2AE]' : ''} ${highlight ? 'bg-emerald-50' : ''}`}
      onClick={isClickable && value > 0 ? onClick : undefined}
    >
      <span className={isClickable && value > 0 ? "text-[#5AA2AE] hover:underline" : "text-slate-900"}>
        {value}
      </span>
    </TableCell>
  );
}

// Segment Verification Dialog - shows when segments are missing
function SegmentVerificationDialog({ stakeholder, open, onClose, segmentTypes, segments, onSave, actionType }) {
  const [formData, setFormData] = useState({ project_title: '', segment_assignments: [] });
  const [saving, setSaving] = useState(false);
  
  useEffect(() => {
    if (stakeholder) {
      setFormData({
        project_title: stakeholder.project_title || '',
        segment_assignments: stakeholder.segment_assignments || []
      });
    }
  }, [stakeholder]);
  
  // Check which segment types are missing
  const missingSegmentTypes = useMemo(() => {
    const assignedTypeIds = new Set((formData.segment_assignments || []).map(sa => sa.segment_type_id));
    return segmentTypes.filter(st => !assignedTypeIds.has(st.id));
  }, [formData.segment_assignments, segmentTypes]);
  
  const handleSegmentChange = (segmentTypeId, segmentId) => {
    setFormData(prev => {
      // Remove any existing assignment for this type
      const filtered = prev.segment_assignments.filter(sa => sa.segment_type_id !== segmentTypeId);
      // Add new assignment if segmentId is provided
      if (segmentId) {
        filtered.push({ segment_type_id: segmentTypeId, segment_id: segmentId });
      }
      return { ...prev, segment_assignments: filtered };
    });
  };
  
  const getSelectedSegmentId = (segmentTypeId) => {
    const assignment = formData.segment_assignments.find(sa => sa.segment_type_id === segmentTypeId);
    return assignment?.segment_id || '';
  };
  
  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(formData, actionType);
      onClose();
    } finally {
      setSaving(false);
    }
  };
  
  if (!stakeholder) return null;
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Verify Stakeholder Segments</DialogTitle>
          <DialogDescription>
            Review and optionally update segments before {actionType === 'invite' ? 'inviting' : 'adding as reference'}.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-3">
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div>
              <Label className="text-[10px] text-slate-500">Name</Label>
              <p className="font-medium">{stakeholder.first_name} {stakeholder.last_name}</p>
            </div>
            <div>
              <Label className="text-[10px] text-slate-500">Email</Label>
              <p className="text-slate-600 truncate">{stakeholder.email || '—'}</p>
            </div>
          </div>
          
          <div>
            <Label htmlFor="title" className="text-xs">Role / Title</Label>
            <Input 
              id="title" 
              value={formData.project_title} 
              onChange={(e) => setFormData(prev => ({ ...prev, project_title: e.target.value }))} 
              className="h-8 text-sm mt-1"
              placeholder="e.g., Project Manager"
            />
          </div>
          
          <div className="space-y-3">
            <Label className="text-xs font-medium">Segments <span className="text-slate-400 font-normal">(optional)</span></Label>
            {segmentTypes.map(st => {
              const isMissing = missingSegmentTypes.some(mst => mst.id === st.id);
              const typeSegments = segments.filter(s => s.segment_type_id === st.id);
              
              return (
                <div key={st.id} className={`p-2 rounded ${isMissing ? 'bg-slate-50 border border-slate-200' : 'bg-slate-50'}`}>
                  <div className="flex items-center justify-between mb-1">
                    <Label className="text-[11px] font-medium">{st.name}</Label>
                    {isMissing && <Badge variant="outline" className="text-[9px] text-slate-500 border-slate-300">Not Set</Badge>}
                  </div>
                  <Select 
                    value={getSelectedSegmentId(st.id) || "none"} 
                    onValueChange={(val) => handleSegmentChange(st.id, val === "none" ? "" : val)}
                  >
                    <SelectTrigger className="h-8 text-sm">
                      <SelectValue placeholder={`Select ${st.name}`} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— None —</SelectItem>
                      {typeSegments.map(seg => (
                        <SelectItem key={seg.id} value={seg.id}>{seg.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              );
            })}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" onClick={handleSave} disabled={saving} className="bg-[#5AA2AE]">
            {saving ? 'Saving...' : actionType === 'invite' ? 'Save & Invite' : 'Save & Add Reference'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Duplicate Resolution Dialog
function DuplicateResolutionDialog({ duplicates, open, onClose, onResolve, segments, segmentTypes }) {
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [resolution, setResolution] = useState('keep_first');
  const [resolving, setResolving] = useState(false);
  
  const currentDuplicate = selectedEmail ? duplicates.find(([email]) => email === selectedEmail) : null;
  const records = currentDuplicate?.[1] || [];
  
  // Helper to get segment names for a stakeholder
  const getSegmentNames = (stakeholder) => {
    return (stakeholder.segment_assignments || []).map(sa => {
      const segment = segments.find(s => s.id === sa.segment_id);
      const segType = segmentTypes.find(st => st.id === sa.segment_type_id);
      if (segment && segType) {
        return { typeName: segType.name, segmentName: segment.name };
      }
      return null;
    }).filter(Boolean);
  };
  
  const handleResolve = async () => {
    if (!currentDuplicate) return;
    setResolving(true);
    try {
      await onResolve(selectedEmail, resolution, records);
      // Move to next duplicate or close
      const remaining = duplicates.filter(([email]) => email !== selectedEmail);
      if (remaining.length > 0) {
        setSelectedEmail(remaining[0][0]);
        setResolution('keep_first');
      } else {
        onClose();
      }
    } finally {
      setResolving(false);
    }
  };
  
  useEffect(() => {
    if (open && duplicates.length > 0 && !selectedEmail) {
      setSelectedEmail(duplicates[0][0]);
    }
  }, [open, duplicates, selectedEmail]);
  
  if (!open || duplicates.length === 0) return null;
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <GitMerge className="h-5 w-5 text-amber-500" />
            Resolve Duplicate Records
          </DialogTitle>
          <DialogDescription>
            Found {duplicates.length} email(s) with multiple records. Review details and select how to resolve.
          </DialogDescription>
        </DialogHeader>
        
        {currentDuplicate && (
          <div className="space-y-4">
            {/* Email selector if multiple duplicates */}
            {duplicates.length > 1 && (
              <div>
                <Label className="text-xs">Select duplicate to resolve:</Label>
                <Select value={selectedEmail} onValueChange={setSelectedEmail}>
                  <SelectTrigger className="h-8 mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {duplicates.map(([email, recs]) => (
                      <SelectItem key={email} value={email}>
                        {email} ({recs.length} records)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {/* Current email being resolved */}
            <div className="bg-slate-50 p-3 rounded-lg">
              <Label className="text-[10px] text-slate-500 uppercase tracking-wide">Duplicate Email</Label>
              <p className="font-medium text-slate-900">{selectedEmail}</p>
            </div>
            
            {/* Records comparison - Enhanced with emails and segments */}
            <div className="space-y-3">
              <Label className="text-xs font-medium">Records to Compare:</Label>
              {records.map((r, i) => {
                const segmentInfo = getSegmentNames(r);
                return (
                  <div key={r.id} className={`border rounded-lg p-3 ${i === 0 ? 'bg-blue-50/50 border-blue-200' : 'bg-white'}`}>
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-[10px]">#{i + 1}</Badge>
                        <span className="font-medium text-sm">{r.first_name} {r.last_name}</span>
                        <Badge className={`text-[9px] ${
                          r.status === 'invited' ? 'bg-emerald-100 text-emerald-700' :
                          r.status === 'pending_approval' ? 'bg-amber-100 text-amber-700' :
                          r.status === 'reference' ? 'bg-slate-100 text-slate-700' :
                          r.status === 'rejected' ? 'bg-red-100 text-red-700' :
                          'bg-slate-100 text-slate-700'
                        }`}>{r.status}</Badge>
                      </div>
                      <Badge variant="outline" className="text-[9px]">
                        {r.source === 'admin_added' ? 'Admin Added' : r.source === 'user_identified' ? 'User Identified' : 'Self Identified'}
                      </Badge>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-3 text-[11px]">
                      <div>
                        <span className="text-slate-500">Email:</span>
                        <span className="ml-1 text-slate-700">{r.email || '—'}</span>
                      </div>
                      <div>
                        <span className="text-slate-500">Title:</span>
                        <span className="ml-1 text-slate-700">{r.project_title || '—'}</span>
                      </div>
                    </div>
                    
                    {/* Segments display */}
                    {segmentInfo.length > 0 ? (
                      <div className="mt-2 pt-2 border-t border-slate-100">
                        <span className="text-[10px] text-slate-500 uppercase tracking-wide">Segments:</span>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {segmentInfo.map((seg, idx) => (
                            <Badge key={idx} variant="outline" className="text-[9px] bg-white">
                              <span className="text-slate-400">{seg.typeName}:</span>
                              <span className="ml-1">{seg.segmentName}</span>
                            </Badge>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="mt-2 pt-2 border-t border-slate-100">
                        <span className="text-[10px] text-slate-400">No segments assigned</span>
                      </div>
                    )}
                    
                    <div className="mt-2 text-[10px] text-slate-400">
                      Created: {new Date(r.created_at).toLocaleDateString()}
                    </div>
                  </div>
                );
              })}
            </div>
            
            {/* Resolution options */}
            <div>
              <Label className="text-xs font-medium mb-2 block">Resolution:</Label>
              <RadioGroup value={resolution} onValueChange={setResolution} className="space-y-2">
                <div className="flex items-center space-x-2 p-2 rounded border hover:bg-slate-50">
                  <RadioGroupItem value="keep_first" id="keep_first" />
                  <Label htmlFor="keep_first" className="text-sm cursor-pointer flex-1">
                    Keep Record #1, reject others
                  </Label>
                </div>
                {records.length > 1 && (
                  <div className="flex items-center space-x-2 p-2 rounded border hover:bg-slate-50">
                    <RadioGroupItem value="keep_second" id="keep_second" />
                    <Label htmlFor="keep_second" className="text-sm cursor-pointer flex-1">
                      Keep Record #2, reject others
                    </Label>
                  </div>
                )}
                <div className="flex items-center space-x-2 p-2 rounded border hover:bg-slate-50">
                  <RadioGroupItem value="keep_all" id="keep_all" />
                  <Label htmlFor="keep_all" className="text-sm cursor-pointer flex-1">
                    Keep all records (not duplicates)
                  </Label>
                </div>
                <div className="flex items-center space-x-2 p-2 rounded border hover:bg-slate-50">
                  <RadioGroupItem value="merge" id="merge" />
                  <Label htmlFor="merge" className="text-sm cursor-pointer flex-1">
                    Merge records (combine all segments, keep first name/email)
                  </Label>
                </div>
              </RadioGroup>
            </div>
          </div>
        )}
        
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose}>Close</Button>
          <Button size="sm" onClick={handleResolve} disabled={resolving} className="bg-[#5AA2AE]">
            {resolving ? 'Resolving...' : `Resolve (${duplicates.length} remaining)`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function PublicStakeholdersSection({ projectId, onRefreshStakeholders }) {
  const { segmentTypes, segments } = useProject();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [period, setPeriod] = useState('daily');
  const [stakeholders, setStakeholders] = useState([]);
  const [invitedStakeholderIds, setInvitedStakeholderIds] = useState(new Set());
  const [filter, setFilter] = useState(null);
  const [processingId, setProcessingId] = useState(null);
  const [segmentDialog, setSegmentDialog] = useState({ open: false, stakeholder: null, actionType: null });
  const [duplicateDialog, setDuplicateDialog] = useState(false);
  
  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, stakeholdersRes, invitationsRes] = await Promise.all([
          stakeholdersAPI.getPublicStats(projectId, period),
          stakeholdersAPI.listAll(projectId),  // Use listAll to get all stakeholders for this component
          invitationsAPI.list(projectId)
        ]);
        setStats(statsRes.data);
        // Handle both paginated and non-paginated response formats
        const stakeholderData = stakeholdersRes.data.stakeholders || stakeholdersRes.data;
        setStakeholders(Array.isArray(stakeholderData) ? stakeholderData : []);
        // Build set of invited stakeholder IDs
        const invIds = new Set(invitationsRes.data.map(inv => inv.stakeholder_id));
        setInvitedStakeholderIds(invIds);
      } catch (err) {
        console.error('Failed to fetch data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [projectId, period]);
  
  // Detect duplicates
  const duplicates = useMemo(() => {
    const emailMap = {};
    stakeholders.forEach(s => {
      if (s.email && !s.is_anonymous) {
        const email = s.email.toLowerCase();
        if (!emailMap[email]) emailMap[email] = [];
        emailMap[email].push(s);
      }
    });
    return Object.entries(emailMap).filter(([_, arr]) => arr.length > 1);
  }, [stakeholders]);
  
  // Helper function to determine source column - MUST match backend logic exactly
  const getSourceColumn = (s) => {
    if (s.is_anonymous) return 'anonymous';
    const source = s.source || '';
    const isSelfIdentified = s.is_self_identified;
    const emailVerified = s.email_verified;
    
    if (source === 'user_identified') return 'user';
    if (source === 'self_identified' || source === 'verified' || isSelfIdentified || emailVerified) return 'self';
    return 'admin'; // Default to admin for admin_added or unknown
  };
  
  // Helper function to determine status row - MUST match backend logic exactly
  const getStatusRow = (s, invitedIds) => {
    const status = s.status || '';
    const isReferenceOnly = s.is_reference_only;
    
    if (status === 'pending_approval') return 'pending_review';
    if (status === 'rejected') return 'rejected';
    if (status === 'invited') return 'invited';
    if (status === 'reference') return 'reference';
    // Legacy support for old status values
    if (status === 'active' || status === 'reference_only') {
      if (isReferenceOnly) return 'reference';
      if (invitedIds.has(s.id)) return 'invited';
      return 'reference';
    }
    return null;
  };

  // Filter stakeholders
  const filteredStakeholders = useMemo(() => {
    if (!filter) return [];
    
    // Special case for duplicates filter
    if (filter.row === 'duplicates') {
      const dupEmails = new Set(duplicates.map(([email]) => email.toLowerCase()));
      return stakeholders.filter(s => s.email && dupEmails.has(s.email.toLowerCase()));
    }
    
    return stakeholders.filter(s => {
      const isAnonymous = s.is_anonymous;
      
      if (filter.row === 'public') return isAnonymous;
      if (isAnonymous) return false;
      
      const col = getSourceColumn(s);
      const row = getStatusRow(s, invitedStakeholderIds);
      
      const colMatch = filter.col === 'total' || filter.col === col;
      
      let rowMatch = false;
      if (filter.row === 'total_stakeholders') {
        // Total row includes pending_review, invited, and reference
        rowMatch = ['pending_review', 'invited', 'reference'].includes(row);
      } else {
        rowMatch = filter.row === row;
      }
      
      return colMatch && rowMatch;
    });
  }, [stakeholders, filter, duplicates, invitedStakeholderIds]);
  
  const refreshData = async () => {
    const [statsRes, stakeholdersRes, invitationsRes] = await Promise.all([
      stakeholdersAPI.getPublicStats(projectId, period),
      stakeholdersAPI.listAll(projectId),
      invitationsAPI.list(projectId)
    ]);
    setStats(statsRes.data);
    // Handle both paginated and non-paginated response formats
    const stakeholderData = stakeholdersRes.data.stakeholders || stakeholdersRes.data;
    setStakeholders(Array.isArray(stakeholderData) ? stakeholderData : []);
    const invIds = new Set(invitationsRes.data.map(inv => inv.stakeholder_id));
    setInvitedStakeholderIds(invIds);
    onRefreshStakeholders?.();
  };
  
  // Check if stakeholder has all segment types assigned
  const hasAllSegments = (stakeholder) => {
    const assignedTypeIds = new Set((stakeholder.segment_assignments || []).map(sa => sa.segment_type_id));
    return segmentTypes.every(st => assignedTypeIds.has(st.id));
  };
  
  // Handle action with segment verification
  const handleActionWithVerification = (stakeholder, actionType) => {
    if (!hasAllSegments(stakeholder)) {
      setSegmentDialog({ open: true, stakeholder, actionType });
    } else {
      performAction(stakeholder, actionType);
    }
  };
  
  const performAction = async (stakeholder, actionType, formData = null) => {
    setProcessingId(stakeholder.id);
    try {
      // Update stakeholder data if formData provided
      if (formData) {
        await stakeholdersAPI.update(projectId, stakeholder.id, formData);
      }
      
      if (actionType === 'invite') {
        await stakeholdersAPI.update(projectId, stakeholder.id, { status: 'invited', is_reference_only: false });
        if (stakeholder.email) await invitationsAPI.create(projectId, stakeholder.id);
        toast.success('Stakeholder invited');
      } else if (actionType === 'reference') {
        await stakeholdersAPI.update(projectId, stakeholder.id, { status: 'reference', is_reference_only: true });
        toast.success('Added as reference');
      } else if (actionType === 'reject') {
        await stakeholdersAPI.update(projectId, stakeholder.id, { status: 'rejected' });
        toast.success('Stakeholder rejected');
      }
      await refreshData();
    } catch (err) {
      toast.error('Action failed');
    } finally {
      setProcessingId(null);
    }
  };
  
  const handleSegmentSave = async (formData, actionType) => {
    if (segmentDialog.stakeholder) {
      await performAction(segmentDialog.stakeholder, actionType, formData);
    }
  };
  
  // Handle duplicate resolution
  const handleDuplicateResolve = async (email, resolution, records) => {
    try {
      if (resolution === 'keep_first') {
        for (let i = 1; i < records.length; i++) {
          await stakeholdersAPI.update(projectId, records[i].id, { status: 'rejected' });
        }
      } else if (resolution === 'keep_second' && records.length > 1) {
        await stakeholdersAPI.update(projectId, records[0].id, { status: 'rejected' });
        for (let i = 2; i < records.length; i++) {
          await stakeholdersAPI.update(projectId, records[i].id, { status: 'rejected' });
        }
      } else if (resolution === 'merge') {
        // Merge: combine segment assignments into first record
        const mergedSegments = [];
        const seenSegTypes = new Set();
        records.forEach(r => {
          (r.segment_assignments || []).forEach(sa => {
            if (!seenSegTypes.has(sa.segment_type_id)) {
              seenSegTypes.add(sa.segment_type_id);
              mergedSegments.push(sa);
            }
          });
        });
        await stakeholdersAPI.update(projectId, records[0].id, { segment_assignments: mergedSegments });
        for (let i = 1; i < records.length; i++) {
          await stakeholdersAPI.update(projectId, records[i].id, { status: 'rejected' });
        }
      }
      // keep_all does nothing
      toast.success('Duplicate resolved');
      await refreshData();
    } catch (err) {
      toast.error('Failed to resolve duplicate');
    }
  };
  
  const getSegmentNames = (s) => (s.segment_assignments || []).map(sa => segments.find(seg => seg.id === sa.segment_id)?.name).filter(Boolean);
  
  const getStatusBadge = (s) => {
    const status = s.status;
    if (status === 'invited') return <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">Invited</Badge>;
    if (status === 'reference') return <Badge className="bg-slate-100 text-slate-700 text-[10px]">Reference</Badge>;
    if (status === 'rejected') return <Badge className="bg-red-100 text-red-700 text-[10px]">Rejected</Badge>;
    if (status === 'pending_approval') return <Badge className="bg-amber-100 text-amber-700 text-[10px]">Pending</Badge>;
    return <Badge variant="outline" className="text-[10px]">{status}</Badge>;
  };
  
  const renderActions = (s) => {
    const isProcessing = processingId === s.id;
    if (filter?.row === 'public' || filter?.row === 'duplicates') return null;
    
    const buttons = [];
    buttons.push(
      <Button key="edit" size="sm" variant="ghost" className="h-6 px-1.5" onClick={() => setSegmentDialog({ open: true, stakeholder: s, actionType: 'edit' })} disabled={isProcessing}>
        <Pencil className="h-3 w-3" />
      </Button>
    );
    
    if (filter?.row === 'pending_review') {
      buttons.push(
        <Button key="reject" size="sm" variant="outline" className="h-6 px-1.5 text-red-600 text-[10px]" onClick={() => performAction(s, 'reject')} disabled={isProcessing}>
          <XCircle className="h-3 w-3 mr-0.5" />Reject
        </Button>,
        <Button key="ref" size="sm" variant="outline" className="h-6 px-1.5 text-[10px]" onClick={() => handleActionWithVerification(s, 'reference')} disabled={isProcessing}>
          <UserCheck className="h-3 w-3 mr-0.5" />Reference
        </Button>,
        <Button key="invite" size="sm" className="h-6 px-1.5 bg-[#5AA2AE] text-[10px]" onClick={() => handleActionWithVerification(s, 'invite')} disabled={isProcessing}>
          <Send className="h-3 w-3 mr-0.5" />Invite
        </Button>
      );
    } else if (filter?.row === 'invited' || (filter?.row === 'total_stakeholders' && s.status === 'invited')) {
      buttons.push(
        <Button key="ref" size="sm" variant="outline" className="h-6 px-1.5 text-[10px]" onClick={() => handleActionWithVerification(s, 'reference')} disabled={isProcessing}>Reference</Button>,
        <Button key="reject" size="sm" variant="outline" className="h-6 px-1.5 text-red-600 text-[10px]" onClick={() => performAction(s, 'reject')} disabled={isProcessing}>Reject</Button>
      );
    } else if (filter?.row === 'reference' || (filter?.row === 'total_stakeholders' && s.status === 'reference')) {
      buttons.push(
        <Button key="invite" size="sm" variant="outline" className="h-6 px-1.5 text-[#5AA2AE] text-[10px]" onClick={() => handleActionWithVerification(s, 'invite')} disabled={isProcessing}>
          <Send className="h-3 w-3 mr-0.5" />Invite
        </Button>,
        <Button key="reject" size="sm" variant="outline" className="h-6 px-1.5 text-red-600 text-[10px]" onClick={() => performAction(s, 'reject')} disabled={isProcessing}>Reject</Button>
      );
    } else if (filter?.row === 'rejected') {
      buttons.push(
        <Button key="invite" size="sm" variant="outline" className="h-6 px-1.5 text-[#5AA2AE] text-[10px]" onClick={() => handleActionWithVerification(s, 'invite')} disabled={isProcessing}>
          <Send className="h-3 w-3 mr-0.5" />Invite
        </Button>,
        <Button key="ref" size="sm" variant="outline" className="h-6 px-1.5 text-[10px]" onClick={() => handleActionWithVerification(s, 'reference')} disabled={isProcessing}>Reference</Button>
      );
    }
    
    return <div className="flex justify-end gap-0.5">{buttons}</div>;
  };
  
  if (loading) return <div className="space-y-4"><Skeleton className="h-64" /><Skeleton className="h-48" /></div>;
  
  const matrix = stats?.matrix || {};
  const inviteLimit = stats?.invite_limit || 100;
  const pendingCount = matrix.pending_review?.total || 0;
  
  return (
    <div className="space-y-4">
      {/* Pending Approval Alert - clickable to filter to pending */}
      {pendingCount > 0 && (
        <Alert 
          className="border-amber-200 bg-amber-50 cursor-pointer hover:bg-amber-100 transition-colors"
          onClick={() => setFilter({ row: 'pending_review', col: 'total' })}
        >
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <AlertTitle className="text-amber-800 text-sm font-semibold">
            Total Pending Review: {pendingCount}
          </AlertTitle>
          <AlertDescription className="text-amber-700 text-xs">
            Click to review and approve pending stakeholders.
          </AlertDescription>
        </Alert>
      )}
      
      {/* Duplicate Warning - clickable to show duplicates */}
      {duplicates.length > 0 && (
        <Alert 
          className="border-orange-200 bg-orange-50 cursor-pointer hover:bg-orange-100 transition-colors"
          onClick={() => {
            setFilter({ row: 'duplicates', col: 'total' });
            setDuplicateDialog(true);
          }}
        >
          <Copy className="h-4 w-4 text-orange-600" />
          <AlertTitle className="text-orange-800 text-sm">
            {duplicates.length} Potential Duplicate(s) Detected
          </AlertTitle>
          <AlertDescription className="text-orange-700 text-xs">
            Click to review and resolve duplicate email addresses.
          </AlertDescription>
        </Alert>
      )}
      
      {/* Matrix Table */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">User Flow Overview</CardTitle>
          <CardDescription className="text-[10px]">Click any number to view and manage stakeholders</CardDescription>
        </CardHeader>
        <CardContent className="pt-0">
          <TooltipProvider>
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="w-28 py-1.5 text-[10px]">Category</TableHead>
                <TableHead className="text-center py-1.5 text-[10px]">Total</TableHead>
                <TableHead className="text-center py-1.5 text-[10px]">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="flex items-center justify-center gap-0.5 cursor-help">
                        Admin <Info className="w-2.5 h-2.5 text-slate-400" />
                      </span>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-[200px] text-xs">
                      <p>These stakeholders were initially identified by the administrator within the Stakeholders section of the Administration portal.</p>
                    </TooltipContent>
                  </Tooltip>
                </TableHead>
                <TableHead className="text-center py-1.5 text-[10px]">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="flex items-center justify-center gap-0.5 cursor-help">
                        User <Info className="w-2.5 h-2.5 text-slate-400" />
                      </span>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-[200px] text-xs">
                      <p>These stakeholders were initially identified by Pulse users using the About Others stakeholder mapping feature.</p>
                    </TooltipContent>
                  </Tooltip>
                </TableHead>
                <TableHead className="text-center py-1.5 text-[10px]">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="flex items-center justify-center gap-0.5 cursor-help">
                        Self <Info className="w-2.5 h-2.5 text-slate-400" />
                      </span>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-[200px] text-xs">
                      <p>These stakeholders were initially identified by users accessing the Public Landing page.</p>
                    </TooltipContent>
                  </Tooltip>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {/* Pending Review - Visually isolated */}
              <TableRow className="bg-amber-50/70 border-b-2 border-amber-200">
                <TableCell className="font-medium py-1.5 text-xs text-amber-700">
                  <span className="flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" />
                    Pending Review
                  </span>
                </TableCell>
                <MatrixCell value={matrix.pending_review?.total} isClickable isSelected={filter?.row === 'pending_review' && filter?.col === 'total'} onClick={() => setFilter({ row: 'pending_review', col: 'total' })} />
                <MatrixCell value={matrix.pending_review?.admin} isClickable isSelected={filter?.row === 'pending_review' && filter?.col === 'admin'} onClick={() => setFilter({ row: 'pending_review', col: 'admin' })} />
                <MatrixCell value={matrix.pending_review?.user} isClickable isSelected={filter?.row === 'pending_review' && filter?.col === 'user'} onClick={() => setFilter({ row: 'pending_review', col: 'user' })} />
                <MatrixCell value={matrix.pending_review?.self} isClickable isSelected={filter?.row === 'pending_review' && filter?.col === 'self'} onClick={() => setFilter({ row: 'pending_review', col: 'self' })} />
              </TableRow>
              
              {/* Spacer row */}
              <TableRow><TableCell colSpan={5} className="h-2 bg-slate-100/50 p-0" /></TableRow>
              
              <TableRow className="bg-emerald-50/50">
                <TableCell className="font-medium py-1.5 text-xs">Invited <Badge variant="outline" className="ml-1 text-[8px] py-0">Limit: {inviteLimit}</Badge></TableCell>
                <MatrixCell value={matrix.invited?.total} isClickable isSelected={filter?.row === 'invited' && filter?.col === 'total'} onClick={() => setFilter({ row: 'invited', col: 'total' })} highlight />
                <MatrixCell value={matrix.invited?.admin} isClickable isSelected={filter?.row === 'invited' && filter?.col === 'admin'} onClick={() => setFilter({ row: 'invited', col: 'admin' })} highlight />
                <MatrixCell value={matrix.invited?.user} isClickable isSelected={filter?.row === 'invited' && filter?.col === 'user'} onClick={() => setFilter({ row: 'invited', col: 'user' })} highlight />
                <MatrixCell value={matrix.invited?.self} isClickable isSelected={filter?.row === 'invited' && filter?.col === 'self'} onClick={() => setFilter({ row: 'invited', col: 'self' })} highlight />
              </TableRow>
              <TableRow>
                <TableCell className="font-medium py-1.5 text-xs">Reference</TableCell>
                <MatrixCell value={matrix.reference?.total} isClickable isSelected={filter?.row === 'reference' && filter?.col === 'total'} onClick={() => setFilter({ row: 'reference', col: 'total' })} />
                <MatrixCell value={matrix.reference?.admin} isClickable isSelected={filter?.row === 'reference' && filter?.col === 'admin'} onClick={() => setFilter({ row: 'reference', col: 'admin' })} />
                <MatrixCell value={matrix.reference?.user} isClickable isSelected={filter?.row === 'reference' && filter?.col === 'user'} onClick={() => setFilter({ row: 'reference', col: 'user' })} />
                <MatrixCell value={matrix.reference?.self} isClickable isSelected={filter?.row === 'reference' && filter?.col === 'self'} onClick={() => setFilter({ row: 'reference', col: 'self' })} />
              </TableRow>
              <TableRow className="border-t-2 bg-slate-50">
                <TableCell className="font-semibold py-1.5 text-xs">Total</TableCell>
                <MatrixCell value={(matrix.invited?.total || 0) + (matrix.reference?.total || 0)} isClickable isSelected={filter?.row === 'total_stakeholders' && filter?.col === 'total'} onClick={() => setFilter({ row: 'total_stakeholders', col: 'total' })} />
                <MatrixCell value={(matrix.invited?.admin || 0) + (matrix.reference?.admin || 0)} isClickable isSelected={filter?.row === 'total_stakeholders' && filter?.col === 'admin'} onClick={() => setFilter({ row: 'total_stakeholders', col: 'admin' })} />
                <MatrixCell value={(matrix.invited?.user || 0) + (matrix.reference?.user || 0)} isClickable isSelected={filter?.row === 'total_stakeholders' && filter?.col === 'user'} onClick={() => setFilter({ row: 'total_stakeholders', col: 'user' })} />
                <MatrixCell value={(matrix.invited?.self || 0) + (matrix.reference?.self || 0)} isClickable isSelected={filter?.row === 'total_stakeholders' && filter?.col === 'self'} onClick={() => setFilter({ row: 'total_stakeholders', col: 'self' })} />
              </TableRow>
              <TableRow><TableCell colSpan={5} className="h-1.5 bg-slate-100 p-0" /></TableRow>
              <TableRow>
                <TableCell className="font-medium py-1.5 text-xs text-red-700">Rejected</TableCell>
                <MatrixCell value={matrix.rejected?.total} isClickable isSelected={filter?.row === 'rejected' && filter?.col === 'total'} onClick={() => setFilter({ row: 'rejected', col: 'total' })} />
                <MatrixCell value={matrix.rejected?.admin} isClickable isSelected={filter?.row === 'rejected' && filter?.col === 'admin'} onClick={() => setFilter({ row: 'rejected', col: 'admin' })} />
                <MatrixCell value={matrix.rejected?.user} isClickable isSelected={filter?.row === 'rejected' && filter?.col === 'user'} onClick={() => setFilter({ row: 'rejected', col: 'user' })} />
                <MatrixCell value={matrix.rejected?.self} isClickable isSelected={filter?.row === 'rejected' && filter?.col === 'self'} onClick={() => setFilter({ row: 'rejected', col: 'self' })} />
              </TableRow>
              <TableRow>
                <TableCell className="font-medium py-1.5 text-xs text-slate-500">Anonymous</TableCell>
                <MatrixCell value={matrix.anonymous?.total || 0} isClickable isSelected={filter?.row === 'anonymous'} onClick={() => setFilter({ row: 'anonymous', col: 'total' })} />
                <TableCell className="text-center text-slate-300 py-1.5 text-sm">-</TableCell>
                <TableCell className="text-center text-slate-300 py-1.5 text-sm">-</TableCell>
                <TableCell className="text-center text-slate-300 py-1.5 text-sm">-</TableCell>
              </TableRow>
            </TableBody>
          </Table>
          </TooltipProvider>
        </CardContent>
      </Card>
      
      {/* Charts Row */}
      <div className="grid grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-1">
            <div className="flex items-center justify-between">
              <CardTitle className="text-xs">User Growth</CardTitle>
              <Select value={period} onValueChange={setPeriod}>
                <SelectTrigger className="w-16 h-6 text-[10px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            <UserGrowthChart 
              data={stats?.growth_data || []} 
              period={period} 
              filter={filter}
              selectedTotal={
                filter ? (
                  filter.row === 'anonymous' 
                    ? (stats?.matrix?.anonymous?.total || 0)
                    : stats?.matrix?.[filter.row]?.[filter.col] || 0
                ) : stats?.matrix?.total_stakeholders?.total || 0
              }
            />
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-1"><CardTitle className="text-xs">Segment Breakdown</CardTitle></CardHeader>
          <CardContent className="pt-0">
            <SegmentBreakdownChart stakeholders={filteredStakeholders} segmentTypes={segmentTypes} segments={segments} filter={filter} />
          </CardContent>
        </Card>
      </div>
      
      {/* Filtered List */}
      {filter && (
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-sm flex items-center gap-2">
                  {filter.row === 'public' ? 'Public Responses' : filter.row === 'duplicates' ? 'Duplicate Records' : 'Stakeholders'}
                  <Badge variant="secondary" className="text-[10px]">{filteredStakeholders.length}</Badge>
                </CardTitle>
                <CardDescription className="text-[10px]">
                  {filter.row === 'pending_review' && 'Review and approve stakeholders'}
                  {filter.row === 'invited' && 'Active invited stakeholders'}
                  {filter.row === 'reference' && 'Reference-only stakeholders'}
                  {filter.row === 'rejected' && 'Rejected stakeholders'}
                  {filter.row === 'public' && 'Public survey responses'}
                  {filter.row === 'total_stakeholders' && 'All stakeholders'}
                  {filter.row === 'duplicates' && 'Records with duplicate email addresses'}
                </CardDescription>
              </div>
              <Button variant="outline" size="sm" onClick={() => setFilter(null)} className="h-6 text-[10px]">
                <X className="h-3 w-3 mr-1" />Clear
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {filteredStakeholders.length === 0 ? (
              <div className="text-center py-6 text-slate-500">
                <Users className="h-8 w-8 mx-auto text-slate-300 mb-2" />
                <p className="text-xs">No stakeholders in this category</p>
              </div>
            ) : filter.row === 'public' ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-[10px]">Submitted</TableHead>
                    <TableHead className="text-[10px]">Location</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredStakeholders.slice(0, 50).map(s => (
                    <TableRow key={s.id}>
                      <TableCell className="text-xs py-1">
                        <div className="flex items-center gap-1 text-slate-600">
                          <Calendar className="h-3 w-3" />
                          {new Date(s.anonymous_metadata?.submitted_at || s.created_at).toLocaleDateString()}
                        </div>
                      </TableCell>
                      <TableCell className="text-xs py-1">
                        <div className="flex items-center gap-1 text-slate-600">
                          <MapPin className="h-3 w-3" />
                          {s.anonymous_metadata?.ip_city || 'Unknown'}, {s.anonymous_metadata?.ip_country || 'Unknown'}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-[10px]">Name</TableHead>
                    <TableHead className="text-[10px]">Email</TableHead>
                    <TableHead className="text-[10px]">Title</TableHead>
                    <TableHead className="text-[10px]">Segments</TableHead>
                    <TableHead className="text-[10px]">Status</TableHead>
                    {filter.row !== 'duplicates' && <TableHead className="text-right text-[10px]">Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredStakeholders.slice(0, 50).map(s => (
                    <TableRow key={s.id}>
                      <TableCell className="font-medium text-xs py-1">{s.first_name} {s.last_name}</TableCell>
                      <TableCell className="text-[9px] text-slate-500 py-1 max-w-[100px] truncate" title={s.email}>{s.email || '—'}</TableCell>
                      <TableCell className="text-[10px] text-slate-500 py-1">{s.project_title || '—'}</TableCell>
                      <TableCell className="py-1">
                        {getSegmentNames(s).length > 0 ? (
                          <div className="flex flex-wrap gap-0.5">
                            {getSegmentNames(s).slice(0, 2).map((name, i) => (
                              <Badge key={i} variant="outline" className="text-[8px] py-0">{name}</Badge>
                            ))}
                            {getSegmentNames(s).length > 2 && <Badge variant="outline" className="text-[8px] py-0">+{getSegmentNames(s).length - 2}</Badge>}
                          </div>
                        ) : <span className="text-slate-300 text-[10px]">—</span>}
                      </TableCell>
                      <TableCell className="py-1">{getStatusBadge(s)}</TableCell>
                      {filter.row !== 'duplicates' && <TableCell className="py-1">{renderActions(s)}</TableCell>}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            {filteredStakeholders.length > 50 && <p className="text-center text-[10px] text-slate-500 mt-1">Showing 50 of {filteredStakeholders.length}</p>}
          </CardContent>
        </Card>
      )}
      
      {/* Segment Verification Dialog */}
      <SegmentVerificationDialog 
        stakeholder={segmentDialog.stakeholder}
        open={segmentDialog.open}
        onClose={() => setSegmentDialog({ open: false, stakeholder: null, actionType: null })}
        segmentTypes={segmentTypes}
        segments={segments}
        onSave={handleSegmentSave}
        actionType={segmentDialog.actionType}
      />
      
      {/* Duplicate Resolution Dialog */}
      <DuplicateResolutionDialog
        duplicates={duplicates}
        open={duplicateDialog}
        onClose={() => setDuplicateDialog(false)}
        onResolve={handleDuplicateResolve}
        segments={segments}
        segmentTypes={segmentTypes}
      />
    </div>
  );
}
